import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import VoiceRecorder from './components/VoiceRecorder';
import FlowchartCanvas from './components/FlowchartCanvas';
import { SpeechRecognitionResult, FlowchartData } from './types';
import { parseVoiceInput, convertToFlowchart, optimizeLayout } from './utils/flowParser';
import './App.css';

// This function handles GitHub Pages 404 redirect parameters
function handleGitHubPagesRedirect() {
  // Parse query parameters
  const query = window.location.search;
  if (!query) return;

  const urlParams = new URLSearchParams(query);
  const path = urlParams.get('p');
  const queryString = urlParams.get('q');
  const hash = urlParams.get('h');

  if (!path) return;

  // Construct the proper URL
  let newUrl = window.location.protocol + '//' + window.location.host;
  
  // Get the repository name from the current path
  const pathParts = window.location.pathname.split('/');
  if (pathParts.length > 1 && pathParts[1]) {
    newUrl += '/' + pathParts[1];
  }
  
  // Add the path, query string, and hash
  newUrl += '/' + path;
  if (queryString) newUrl += '?' + queryString;
  if (hash) newUrl += '#' + hash;

  // Replace the current URL without reloading the page
  window.history.replaceState(null, '', newUrl);
}

function App() {
  const [isListening, setIsListening] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [flowchartData, setFlowchartData] = useState<FlowchartData>({ nodes: [], edges: [] });
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Debounce timer for real-time processing
  const processingTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track the last processed transcript to avoid unnecessary updates
  const lastProcessedTranscript = useRef<string>('');
  
  // Track the last update time to prevent too frequent updates
  const lastUpdateTimeRef = useRef<number>(0);
  const minUpdateInterval = 800; // Minimum time between updates in ms

  // Handle GitHub Pages 404 redirect on initial load
  useEffect(() => {
    handleGitHubPagesRedirect();
  }, []);

  const handleResultsUpdate = useCallback((result: SpeechRecognitionResult) => {
    if (result.isFinal) {
      setCurrentTranscript(prev => `${prev} ${result.transcript}`.trim());
    }
  }, []);

  const handleVoiceInputComplete = useCallback((transcript: string) => {
    if (!transcript) return;
    
    // Skip processing if the transcript hasn't changed significantly
    // This helps reduce processing load for minor changes
    if (transcript.length > 0 && 
        lastProcessedTranscript.current.length > 0 &&
        Math.abs(transcript.length - lastProcessedTranscript.current.length) < 5) {
      return;
    }
    
    // Enforce minimum time between updates to prevent flashing
    const now = Date.now();
    if (now - lastUpdateTimeRef.current < minUpdateInterval) {
      // If we're updating too frequently, delay the update
      if (processingTimerRef.current) {
        clearTimeout(processingTimerRef.current);
      }
      
      processingTimerRef.current = setTimeout(() => {
        handleVoiceInputComplete(transcript);
      }, minUpdateInterval);
      
      return;
    }
    
    // Clear any existing timer
    if (processingTimerRef.current) {
      clearTimeout(processingTimerRef.current);
    }
    
    // Set a debounced timer to avoid excessive processing during continuous speech
    processingTimerRef.current = setTimeout(() => {
      setIsProcessing(true);
      lastUpdateTimeRef.current = Date.now();
      
      // Process transcript into flowchart data
      try {
        lastProcessedTranscript.current = transcript;
        const parsedItems = parseVoiceInput(transcript);
        const initialFlowchart = convertToFlowchart(parsedItems);
        const optimizedFlowchart = optimizeLayout(initialFlowchart);
        
        setFlowchartData(optimizedFlowchart);
      } catch (error) {
        console.error('Error processing voice input:', error);
      } finally {
        setIsProcessing(false);
      }
    }, 500); // Increased to 500ms for better performance
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (processingTimerRef.current) {
        clearTimeout(processingTimerRef.current);
      }
    };
  }, []);

  // Memoize the flowchart data to prevent unnecessary re-renders
  const memoizedFlowchartData = useMemo(() => flowchartData, [
    // Only update if number of nodes or edges changes
    flowchartData.nodes.length,
    flowchartData.edges.length,
    // or if the first node or last node's text changes
    flowchartData.nodes[0]?.data?.label,
    flowchartData.nodes[flowchartData.nodes.length - 1]?.data?.label
  ]);

  const handleFlowchartDataChange = useCallback((newData: FlowchartData) => {
    setFlowchartData(newData);
  }, []);

  // Check for redirect on initial load
  useEffect(() => {
    const redirect = sessionStorage.getItem('redirect');
    if (redirect) {
      sessionStorage.removeItem('redirect');
      // Handle routing here if needed
      console.log('Redirected from:', redirect);
    }
  }, []);

  return (
    <div className="App">
      <header className="App-header">
        <h1>Voice Flowchart Generator</h1>
        <p className="App-subtitle">
          Speak your workflow and watch it transform into a flowchart in real-time
        </p>
      </header>
      
      <main className="App-main">
        <section className="voice-section">
          <VoiceRecorder
            onResultsUpdate={handleResultsUpdate}
            onVoiceInputComplete={handleVoiceInputComplete}
            isListening={isListening}
            setIsListening={setIsListening}
          />
          
          {isProcessing && (
            <div className="processing-indicator">
              <div className="spinner"></div>
              <p>Processing your voice input...</p>
            </div>
          )}
          
          <div className="instructions">
            <h3>How to use:</h3>
            <ol>
              <li>Click the microphone button to start recording</li>
              <li>Describe your workflow with sequential steps and decision points</li>
              <li>Watch as your flowchart is generated in real-time</li>
              <li>Click the button again to stop when finished</li>
              <li>Edit the flowchart or export it in various formats</li>
            </ol>
            <div className="tips">
              <h4>Tips for best results:</h4>
              <ul>
                <li>Use clear sequential terms like "first", "then", "next"</li>
                <li>For decision points, use phrases like "if", "when", or "check if"</li>
                <li>Speak clearly and pause briefly between steps</li>
              </ul>
            </div>
          </div>
        </section>
        
        <section className="flowchart-section">
          <FlowchartCanvas 
            flowchartData={memoizedFlowchartData}
            onFlowchartDataChange={handleFlowchartDataChange}
          />
        </section>
      </main>
      
      <footer className="App-footer">
        <p>&copy; {new Date().getFullYear()} Voice Flowchart Generator</p>
      </footer>
    </div>
  );
}

export default App;
